/** TREPTELE, MÂINILE CURENTE ȘI PLATFORMA de la intrarea de la teren, în 3D
 *  (format 3.12) — geometrii pure, puse de machete în scenele lor. Fiecare
 *  treaptă e un bloc plin de la placa de jos până la fața ei, ca scara
 *  nucleului (`interior3d.treptele`); cadrul e cel al planșei (`treptePlan`),
 *  deci o treaptă stă în machetă exact unde o desenează planul. */

import * as THREE from "three";
import { cadruDreptunghi, cadruTrepte, dinCadru } from "./treptePlan";

/** Înălțimea mâinii curente peste linia treptelor. */
export const MANA_CURENTA_M = 0.9;
/** PARDOSEALA FINITĂ a plăcii coborâte, peste fața ei de beton: pe ea pornesc
 *  treptele și platforma. Și PESTE TOATE STRATURILE CURȚII (`RandareSit3D`:
 *  lotul 1 cm, drumurile 2, trotuarele 3, verdele 4): ele trec și pe sub
 *  clădire, iar în scobitura intrării, deschisă spre curte, o pardoseală la
 *  cota trotuarului se bătea cu el. */
export const FINISAJ_M = 0.05;
/** Pardoseala platformei peste placa pe care stă: nu în planul ei. */
const PLATFORMA_PLACA_M = 0.06;
const PLATFORMA_BALUSTRADA_M = 1.0;
const BARA_M = 0.04;

const cutie = (dx: number, dy: number, dz: number, cx: number, cy: number, cz: number, deg: number,
               tilt = 0): THREE.BufferGeometry => {
  const g = new THREE.BoxGeometry(dx, dy, dz);
  if (tilt) g.rotateY(tilt);
  g.rotateZ((deg * Math.PI) / 180);
  g.translate(cx, cy, cz);
  return g;
};

/** Blocurile treptelor: `n` contratrepte de la `cota0` până la `cota0 + h`,
 *  pe amprenta `poly`, urcând pe `deg`. Ultima contratreaptă dă în placa de
 *  sus, deci blocurile sunt `n − 1`, din ce în ce mai înalte. */
export function treptePeAmprenta(poly: readonly number[][], deg: number, cota0: number, h: number,
                                 n: number): THREE.BufferGeometry[] {
  const c = cadruTrepte(poly, deg);
  if (!c || n < 2 || h <= 0) return [];
  const pas = (c.s1 - c.s0) / (n - 1), lat = c.t1 - c.t0, tm = (c.t0 + c.t1) / 2;
  const out: THREE.BufferGeometry[] = [];
  for (let i = 1; i < n; i++) {
    const hi = (h * i) / n;
    const [cx, cy] = dinCadru(c, c.s0 + (i - 0.5) * pas, tm);
    out.push(cutie(pas, lat, hi, cx, cy, cota0 + hi / 2, deg));
  }
  return out;
}

/** Mâinile curente ale rampei: o bară pe fiecare margine, înclinată cu rampa,
 *  la `MANA_CURENTA_M` peste linia treptelor, cu un montant la fiecare capăt. */
export function mainiCurente(poly: readonly number[][], deg: number, cota0: number, h: number,
                             n: number): THREE.BufferGeometry[] {
  const c = cadruTrepte(poly, deg);
  if (!c || n < 2 || h <= 0) return [];
  // linia treptelor urcă de la fața primei trepte (h/n, la s0) la placa de sus (h, la s1)
  const L = c.s1 - c.s0, dz = h - h / n;
  const lung = Math.hypot(L, dz), tilt = -Math.atan2(dz, L);
  const out: THREE.BufferGeometry[] = [];
  for (const t of [c.t0 + BARA_M, c.t1 - BARA_M]) {
    const [cx, cy] = dinCadru(c, (c.s0 + c.s1) / 2, t);
    out.push(cutie(lung, BARA_M, BARA_M, cx, cy, cota0 + h / n + dz / 2 + MANA_CURENTA_M, deg, tilt));
    for (const [s, z] of [[c.s0 + BARA_M, cota0 + h / n], [c.s1 - BARA_M, cota0 + h]] as const) {
      const [px, py] = dinCadru(c, s, t);
      out.push(cutie(BARA_M, BARA_M, MANA_CURENTA_M, px, py, z + MANA_CURENTA_M / 2, deg));
    }
  }
  return out;
}

/** Platforma elevatoare, în repaus jos: pardoseala ei peste placă și
 *  balustrada pe toate laturile (montanți în colțuri, bară sus). Întoarce
 *  geometriile ei pe două liste: placa și metalul. */
export function platforma3d(poly: readonly number[][], cota0: number):
    { placa: THREE.BufferGeometry[]; metal: THREE.BufferGeometry[] } {
  const d = cadruDreptunghi(poly);
  if (!d) return { placa: [], metal: [] };
  const placa = [cutie(d.w, d.l, PLATFORMA_PLACA_M, d.cx, d.cy, cota0 + PLATFORMA_PLACA_M / 2, d.deg)];
  const metal: THREE.BufferGeometry[] = [];
  const a = (d.deg * Math.PI) / 180, co = Math.cos(a), si = Math.sin(a);
  const colt = (sx: number, sy: number): [number, number] =>
    [d.cx + (sx * d.w / 2) * co - (sy * d.l / 2) * si, d.cy + (sx * d.w / 2) * si + (sy * d.l / 2) * co];
  for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
    const [px, py] = colt(sx * (1 - BARA_M / d.w), sy * (1 - BARA_M / d.l));
    metal.push(cutie(BARA_M, BARA_M, PLATFORMA_BALUSTRADA_M, px, py,
                     cota0 + PLATFORMA_PLACA_M + PLATFORMA_BALUSTRADA_M / 2, d.deg));
  }
  const zBara = cota0 + PLATFORMA_PLACA_M + PLATFORMA_BALUSTRADA_M - BARA_M / 2;
  for (const sy of [-1, 1]) {
    const [px, py] = colt(0, sy * (1 - BARA_M / d.l));
    metal.push(cutie(d.w, BARA_M, BARA_M, px, py, zBara, d.deg));
  }
  for (const sx of [-1, 1]) {
    const [px, py] = colt(sx * (1 - BARA_M / d.w), 0);
    metal.push(cutie(BARA_M, d.l, BARA_M, px, py, zBara, d.deg));
  }
  return { placa, metal };
}
